import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import { fNumber } from 'src/utils/format-number';

import Label from 'src/components/label';

import { IReorderSettingsRow, ReorderSettingsItemType } from 'src/types/reorder-settings';

type Props = {
  row: IReorderSettingsRow;
};

const ITEM_TYPE_LABEL: Record<ReorderSettingsItemType, string> = {
  inventory: 'Inventory',
  packaging: 'Packaging Item',
  stationery: 'Stationery',
};

const ITEM_TYPE_COLOR: Record<ReorderSettingsItemType, 'info' | 'primary' | 'secondary'> = {
  inventory: 'info',
  packaging: 'primary',
  stationery: 'secondary',
};

export default function MyTasksCard({ row }: Props) {
  const { itemType, itemCode, itemName, quantity, newOrderQty, orderBy, unit } = row;

  const unitLabel = unit?.abbreviation ?? '';
  const isOutOfStock = row.stockStatus === 'Out of Stock';

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>
            {itemName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {itemCode}
          </Typography>
        </Stack>

        <Label variant="soft" color={isOutOfStock ? 'error' : 'warning'}>
          {row.stockStatus}
        </Label>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Label variant="soft" color={ITEM_TYPE_COLOR[itemType]}>
          {ITEM_TYPE_LABEL[itemType]}
        </Label>
      </Stack>

      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
        sx={{ mt: 2 }}
      >
        <Stack sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Current Qty
          </Typography>
          <Typography variant="h6" sx={{ color: isOutOfStock ? 'error.main' : 'warning.main' }}>
            {fNumber(quantity)} {unitLabel}
          </Typography>
        </Stack>

        <Stack sx={{ flex: 1, pl: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Reorder Qty
          </Typography>
          <Typography variant="h6">
            {newOrderQty !== null ? `${fNumber(newOrderQty)} ${unitLabel}` : 'Not set'}
          </Typography>
        </Stack>
      </Stack>

      {orderBy && (
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 2, display: 'block' }}>
          Assigned to {orderBy.firstName} {orderBy.lastName ?? ''}
        </Typography>
      )}
    </Card>
  );
}
